/**
 * Wire types for the `LeadQuote` document stored in Cosmos by dave-service, and
 * the display types the public quote screen renders.
 *
 * Field names mirror the `[JsonProperty(...)]` names in
 * `data-model/Storage/*.cs` exactly. Do not "tidy" them into camelCase here —
 * these are the bytes on the wire. Presentation-shaped types live at the bottom
 * of this file and are produced by the mappers in `@ir/services`.
 */

/* -------------------------------------------------------------------------- */
/* Primitives                                                                  */
/* -------------------------------------------------------------------------- */

export const COVER_TYPES = [
  "Comprehensive",
  "Third Party, Fire and Theft",
  "Third Party Only",
] as const;

export type CoverType = (typeof COVER_TYPES)[number];

/** Keys used for cover pricing in the Cosmos document. */
export type CoverKey = "comprehensive" | "tpft" | "tp";

export const COVER_KEY_BY_TYPE: Record<CoverType, CoverKey> = {
  Comprehensive: "comprehensive",
  "Third Party, Fire and Theft": "tpft",
  "Third Party Only": "tp",
};

export const COVER_TYPE_BY_KEY: Record<CoverKey, CoverType> = {
  comprehensive: "Comprehensive",
  tpft: "Third Party, Fire and Theft",
  tp: "Third Party Only",
};

export type PaymentType = "Monthly" | "Annually";

export type Instalments = 10 | 11;

/** Lifecycle states a quote can be in, as used by the admin state filter. */
export const QUOTE_STATES = [
  "Pending",
  "Quoted",
  "Paid",
  "Processed",
  "Cancelled",
  "Failed",
] as const;

export type QuoteState = (typeof QUOTE_STATES)[number];

/* -------------------------------------------------------------------------- */
/* Risk document sub-shapes                                                    */
/* -------------------------------------------------------------------------- */

export interface Address {
  house_number?: string | null;
  street?: string | null;
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  line4?: string | null;
  city?: string | null;
  county?: string | null;
  country?: string | null;
  postcode?: string | null;
}

export interface Licence {
  type?: string | null;
  date?: string | null;
  country?: string | null;
  number?: string | null;
}

export interface Occupation {
  type?: string | null;
  business?: string | null;
  status?: string | null;
  part_time?: boolean | null;
}

export interface Conviction {
  type?: string | null;
  date?: string | null;
  is_fined?: boolean | null;
  fine?: number | null;
  is_disqualified?: boolean | null;
  disqualification?: number | null;
  points?: number | null;
  details?: string | null;
  sample?: string | null;
  blood?: string | null;
  breath?: string | null;
  urine?: string | null;
}

export interface CriminalConviction {
  date?: string | null;
  reason?: string | null;
  /** Serialised as a string on the wire, but read as a yes/no flag. */
  received?: string | boolean | null;
}

export interface MedicalCondition {
  type?: string | null;
  date?: string | null;
  dvla_notified?: boolean | null;
}

export interface Incident {
  date?: string | null;
  claim?: string | null;
  type?: string | null;
  fault?: boolean | null;
  ncb_affected?: boolean | null;
  bodily_injured?: boolean | null;
  policy?: string | null;
  cost?: number | null;
}

export interface Driver {
  title?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  date_of_birth?: string | null;
  marital_status?: string | null;
  gender?: string | null;
  home_owner?: boolean | null;
  telephone?: string | null;
  email?: string | null;
  relationship?: string | null;
  address?: Address | null;
  licence?: Licence | null;
  occupation?: Occupation[] | null;
  conviction?: Conviction[] | null;
  incident?: Incident[] | null;
  criminal_conviction?: CriminalConviction[] | null;
  medical_condition?: MedicalCondition[] | null;
  is_in_uk_since_birth?: boolean | null;
  uk_since?: string | null;
  driving_other_vehicle?: boolean | null;
  driving_status?: string | null;
  business_miles?: number | null;
  personal_use?: boolean | null;
  personal_miles?: number | null;
  commuting?: boolean | null;
  business_use?: boolean | null;
  has_criminal_conviction?: boolean | null;
  has_conviction?: boolean | null;
  has_insurance_refused?: boolean | null;
  has_occupation?: boolean | null;
  has_incident?: boolean | null;
  has_medical_condition?: boolean | null;
  insurance_refused?: string | null;
  disabled?: boolean | null;
}

export interface Cover {
  date?: string | null;
  type?: string | null;
}

export interface Ncb {
  have?: boolean | null;
  years?: number | null;
  protected?: boolean | null;
  type?: string | null;
  mirror?: boolean | null;
  current_policy_end_date?: string | null;
  policy_expire_date?: string | null;
}

export interface Modification {
  type?: string | null;
}

export interface Vehicle {
  vrm?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  abi?: number | null;
  abi_group?: number | null;
  cover?: Cover | null;
  ncb?: Ncb | null;
  purchase_date?: string | null;
  value?: number | null;
  owner?: string | null;
  keeper?: string | null;
  owns_vehicle?: boolean | null;
  overnight_same_address?: boolean | null;
  overnight_location?: string | null;
  overnight_location_postcode?: string | null;
  has_modification?: boolean | null;
  modification?: Modification[] | null;
  has_security?: boolean | null;
  seats?: number | null;
  doors?: number | null;
  body_type?: string | null;
  engine?: number | null;
  fuel_type?: string | null;
  transmission?: string | null;
  weight?: number | null;
  is_imported?: boolean | null;
  is_left_hand_drive?: boolean | null;
  carried_hazardous?: boolean | null;
  carries_signage?: boolean | null;
  use?: string | null;
  payment_type?: string | null;
  is_leased?: boolean | null;
  leased_period?: number | null;
}

export interface Risk {
  credit_score?: number | null;
  id_score?: number | null;
  ccj?: string | null;
  wind_screen?: boolean | null;
  driver?: Driver[] | null;
  vehicle?: Vehicle[] | null;
}

/* -------------------------------------------------------------------------- */
/* Pricing                                                                     */
/* -------------------------------------------------------------------------- */

export interface FinanceOptionResult {
  fullPayment: number;
  deposit: number;
  amountToFinance: number;
  interest: number;
  /** Fractional, e.g. 0.2495 — multiply by 100 for display. */
  interestRate: number;
  totalCostOfLoan: number;
  monthlyRepayment: number;
  numberOfMonths: number;
  commissionAmount: number;
}

export interface PremiumBreakdown {
  basePremiumTotal: number;
  legalFees: number;
  brokerArrangementFee: number;
  finance?: FinanceOptionResult | null;
}

export interface FinanceCost {
  initialPayment: number;
  monthlyPayment: number;
  totalPayable: number;
  breakdown?: PremiumBreakdown | null;
}

export interface FinanceOptions {
  elevenMonths: FinanceCost;
  tenMonths: FinanceCost;
  annually: number;
}

export type CoverCosts = Partial<Record<CoverKey, FinanceOptions>>;

export type ExcessByCover = Partial<Record<CoverKey, number>>;

/* -------------------------------------------------------------------------- */
/* Provider quotes                                                             */
/* -------------------------------------------------------------------------- */

export interface NonBrokerQuoteDetail {
  uuid?: string | null;
  parentUuid?: string | null;
  insurer?: string | null;
  status?: string | null;
  state?: string | null;
  no_quote_reason?: string | null;
  business_type?: string | null;
  ncb?: number | null;
  cover?: CoverCosts | null;
  excess?: ExcessByCover | null;
}

export interface BrokerQuoteDetail {
  uuid?: string | null;
  parentUuid?: string | null;
  state?: string | null;
  premium?: number | null;
  cover_type?: string | null;
  is_decline?: boolean | null;
  is_refer?: boolean | null;
  refer_reason?: string[] | null;
  decline_reason?: string[] | null;
  highlight?: boolean | null;
  mta_can_be_accepted?: boolean | null;
  voluntary_excess?: number | null;
  cancellation_balance?: number | null;
  mta_balance?: number | null;
  compulsory_excess?: string[] | null;
  fees?: string[] | null;
  rates?: string[] | null;
  endorsements?: string[] | null;
  conditions?: string[] | null;
  info?: Record<string, unknown> | null;
  expiration_date?: string | null;
}

export interface OgiInsurerQuote {
  sequence?: string | null;
  decision?: string | null;
  reason?: string | null;
  insurer_name?: string | null;
  scheme_reference?: string | null;
  scheme_id?: string | null;
}

export interface OgiMetadata {
  submission_mode?: string | null;
  successful_path?: string | null;
  hosted_quotes_attempted?: boolean | null;
  instream_attempted?: boolean | null;
  instream_status?: string | null;
  instream_submission_key?: string | null;
  prospect_reference?: string | null;
  instream_error_message?: string | null;
  requestor_id?: string | null;
  broker_reference?: string | null;
  quote_reference?: string | null;
  select_list?: number | null;
  selected_sequence?: number | null;
  active_quote_call_success?: boolean | null;
  send_to_broker_status?: string | null;
  send_to_broker_successful?: boolean | null;
  send_to_broker_ref?: string | null;
  send_to_broker_description?: string | null;
  send_to_broker_error_id?: string | null;
  send_to_broker_error_message?: string | null;
  broker_policy_reference?: string | null;
  insurer_quotes?: OgiInsurerQuote[] | null;
}

export interface ProviderMetadata {
  ogi?: OgiMetadata | null;
}

export interface BuyIntent {
  status: "Processing" | "PendingRetry" | "Queued" | "Failed" | string;
  selected_at: string;
  updated_at: string;
  queued_at?: string | null;
  attempt_count?: number | null;
  last_attempt_at?: string | null;
  next_retry_at?: string | null;
  provider: string;
  insurer: string;
  cover_type: string;
  provider_reference?: string | null;
  online_reference?: string | null;
  annual_premium: number;
  payment_type?: string | null;
  instalments?: number | null;
  noetica_status?: string | null;
  noetica_customer_id?: string | null;
  noetica_queue_id?: number | null;
  error?: string | null;
}

export interface NoeticaDelivery {
  status: "PendingRetry" | "Queued" | "Failed" | string;
  attempt_count: number;
  last_attempt_at?: string | null;
  next_retry_at?: string | null;
  queued_at?: string | null;
  noetica_customer_id?: string | null;
  noetica_queue_id?: number | null;
  error?: string | null;
}

export interface BaseQuote {
  id?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  parentUuid?: string | null;
  provider?: string | null;
  ref?: string | null;
  online_ref?: string | null;
  state?: string | null;
  error?: string | null;
  non_broker_quote_details?: NonBrokerQuoteDetail | null;
  broker_quote_details?: BrokerQuoteDetail[] | null;
  provider_metadata?: ProviderMetadata | null;
  ncb?: Ncb | null;
}

/* -------------------------------------------------------------------------- */
/* The root document                                                           */
/* -------------------------------------------------------------------------- */

export interface LeadQuote {
  id: string;
  type?: string | null;
  user?: string | null;
  state?: string | null;
  reference?: string | null;
  source?: string | null;
  leela_id?: string | null;
  noetica_customer_id?: string | null;
  dialer_queue?: string | null;
  sent_to_dialer_time?: string | null;
  noetica_delivery?: NoeticaDelivery | null;
  created_at?: string | null;
  updated_at?: string | null;
  is_quotable_online?: boolean | null;
  ccj?: string | null;
  wind_screen?: boolean | null;
  granite_sent?: boolean | null;
  ogi_sent?: boolean | null;
  customer_quote_email_sent_at?: string | null;
  customer_quote_sms_sent_at?: string | null;
  customer_pricing?: CustomerPricingSnapshot[] | null;
  customer_quote_notification_claimed_at?: string | null;
  customer_quote_notification_trigger?: string | null;
  marketplace_quotes_ready_at?: string | null;
  marketplace_reconciliation_scheduled_at?: string | null;
  marketplace_reconciliation_deadline_at?: string | null;
  marketplace_reconciliation_processed_at?: string | null;
  buy_intent?: BuyIntent | null;
  driver?: Driver[] | null;
  vehicle?: Vehicle[] | null;
  risk?: Risk | null;
  base_quotes?: BaseQuote[] | null;
  queryParams?: Record<string, string> | null;
}

/* -------------------------------------------------------------------------- */
/* Presentation types (public quote screen)                                    */
/* -------------------------------------------------------------------------- */

export interface PriceInfo {
  initialPayment: number;
  monthlyPayment: number;
  totalPayable: number;
  customerPricing?: CustomerFinanceBreakdown | null;
}

export interface CoverTypePrice {
  elevenMonths: PriceInfo;
  tenMonths: PriceInfo;
  annually: number;
  rawAnnually?: number;
}

export interface CustomerFinanceBreakdown {
  instalments: 10 | 11;
  firstPaymentTiming: string;
  depositRate: number;
  transactionFeeRate: number;
  loanAmount: number;
  initialPayment: number;
  transactionFee: number;
  monthlyPayment: number;
  totalPayable: number;
}

export interface CustomerPricingBreakdown {
  provider: string;
  insurerPremium: number;
  insurerFeeBeforeCommission: number;
  iptRate: number;
  displayedStandardCommissionRate: number;
  salesIncomeCommissionRate: number;
  motorLegalFee: number;
  arrangementFee: number;
  feeInLieuRate: number;
  feeInLieuAmount: number;
  totalSellingPrice: number;
  premiumCommission: number;
  feesIncome: number;
  financeCommission: number;
  totalSalesIncome: number;
  finance?: CustomerFinanceBreakdown | null;
}

export interface CustomerFinanceSnapshot {
  instalments: 10 | 11;
  first_payment_timing: string;
  deposit_rate: number;
  initial_payment: number;
  loan_amount: number;
  transaction_fee_rate: number;
  transaction_fee: number;
  monthly_payment: number;
  total_payable: number;
  finance_commission: number;
  total_sales_income: number;
}

export interface CustomerPricingSnapshot {
  provider: string;
  insurer: string;
  cover_type: string;
  provider_reference: string;
  insurer_premium: number;
  insurer_fee_before_commission?: number;
  motor_legal_fee: number;
  arrangement_fee: number;
  fee_in_lieu_rate: number;
  fee_in_lieu_amount: number;
  total_selling_price: number;
  premium_commission: number;
  fees_income: number;
  paid_in_full_sales_income: number;
  ten_months?: CustomerFinanceSnapshot | null;
  eleven_months?: CustomerFinanceSnapshot | null;
  calculated_at: string;
}

export type QuoteCategory = "cheapest" | "bestComprehensive" | "bestCover";

/** One selectable quote row on the public screen. */
export interface CustomerQuote {
  isIndicative?: boolean;
  provider: string;
  insurer: string;
  coverType: CoverType;
  excess: number;
  compulsoryExcess?: number;
  motorLegalExpense?: number;
  guaranteedHireVehicle?: number;
  discountPremium?: number;
  overallDiscount?: number;
  providerReference?: string;
  onlineReference?: string;
  expirationDate?: string | null;
  annualOnly?: boolean;
  endorsements?: string[];
  conditions?: string[];
  category?: QuoteCategory;
  customerPricing?: CustomerPricingBreakdown;
  price: Partial<Record<CoverKey, CoverTypePrice>>;
}

export interface CustomerDriverInfo {
  first_name?: string;
  last_name?: string;
  email?: string;
  telephone?: string;
  address?: Address | null;
}

export interface CustomerVehicleInfo {
  make?: string;
  model?: string;
  vrm?: string;
  year?: number | null;
}

export interface ProviderSummary {
  provider: string;
  state?: string | null;
  quoteCount: number;
  hasCustomerQuotes: boolean;
  isResolved: boolean;
  isExpectedPricingProvider: boolean;
}

export interface MarketplacePricingSummary {
  expectedProviderCount: number;
  quotedProviderCount: number;
  resolvedProviderCount: number;
  missingProviders: string[];
  waitingProviders: string[];
  providersWithoutQuotes: string[];
  providerBreakdown: Array<{
    provider: string;
    returnedQuote: boolean;
  }>;
  graniteHasResponded: boolean;
  customerViewReady: boolean;
  allExpectedProvidersResolved: boolean;
  ogiSentSuccessfully: boolean;
  ogiStatus?: string | null;
  overallStatus: "Pending" | "Partial" | "Quoted" | "Failed";
}

/** Everything the public screen needs for one quote reference. */
export interface QuoteReference {
  reference: string;
  quoteId?: string;
  quotes: CustomerQuote[];
  driverInfo?: CustomerDriverInfo;
  vehicleInfo?: CustomerVehicleInfo;
  marketplaceReady: boolean;
  pricingSummary: MarketplacePricingSummary;
  providerSummaries: ProviderSummary[];
}

export interface PollResult {
  data: QuoteReference | null;
  isPolling: boolean;
  elapsedTime: number;
  timeoutError?: string;
}
